import {
  Draft,
  DraftBendingTextConfiguration,
  DraftConfiguration,
  DraftDesignConfiguration,
  DraftSpecialTextConfiguration,
  DraftTextConfiguration,
  SharingDraft,
  SharingDraftConfiguration,
  SharingDraftConfigurationWithText,
  SharingDraftDesignConfiguration,
  SharingDraftSpecialTextConfiguration,
  StoreFont,
  StoreSpecialFont,
  TextAlign,
} from '@/types';
import { MM_PER_INCH } from '@/utils/math/consts';
import { isEqualPoints } from '@/utils/math/isEqualPoints';
import { textAlignToAnchor } from '@/utils/text/textAlignToAnchor';
import { getConfigurations } from '@/model/draft/getConfigurations';
import { getRotationFromTransform } from '@/model/draftMapper/utils/getRotationFromTransform';
import {
  isSharedConfigurationFilterWithId,
  isSharingDraftBendingTextConfiguration,
  isSharingDraftConfigurationWithTextOrSpecialText,
  isSharingDraftDesignConfiguration,
  isSharingDraftPlaceholderTextConfiguration,
  isSharingDraftSpecialTextConfiguration,
  isSharingDraftTextConfiguration,
} from '@/model/draftMapper/utils/isSharingDraftConfigurationType';

const hasDifferentColors = (
  a: Exclude<SharingDraftConfiguration, SharingDraftSpecialTextConfiguration>,
  b: DraftConfiguration
) => {
  if (
    a.printColorRGBs &&
    b.printColors &&
    a.printColorRGBs !== b.printColors.map((p) => p.fill).join(' ')
  ) {
    return true;
  }

  if (
    a.printColorId &&
    b.printColors &&
    a.printColorId !== b.printColors.map((p) => p.id).join(' ')
  ) {
    return true;
  }

  return false;
};

const isSameStoreFont = (
  { fontFamilyId, fontId }: SharingDraftConfigurationWithText,
  font: StoreFont | StoreSpecialFont
): font is StoreFont => `sprdfont_${fontFamilyId}_${fontId}` === font.identifier;

function isSameDesignConfiguration(
  a: SharingDraftDesignConfiguration,
  b: DraftDesignConfiguration
) {
  if (a.designId !== b.design.id) {
    return false;
  }

  if (hasDifferentColors(a, b)) {
    return false;
  }

  // check fitler settings
  if (a.filter && !isSharedConfigurationFilterWithId(a.filter) && b.filter && b.filter.settings) {
    const settingsA = Object.entries(a.filter.settings);
    const settingsB = Object.entries(b.filter.settings);
    if (
      settingsA.length !== settingsB.length ||
      settingsA.some(
        ([key, value], index) => settingsB[index][0] !== key || settingsB[index][1] !== value
      )
    ) {
      return false;
    }
  }

  // compare mask specific stuff
  if (a.mask && b.mask) {
    if (a.mask.id !== b.mask.id) {
      return false;
    }
    if (!isEqualPoints(a.mask.offset, b.mask.offset)) {
      return false;
    }
    if (!isEqualPoints(a.mask.scale, b.mask.scale)) {
      return false;
    }
  }

  return true;
}

function isSameTextConfiguration(
  a: Exclude<SharingDraftConfiguration, SharingDraftDesignConfiguration>,
  b: Exclude<DraftConfiguration, DraftDesignConfiguration>
) {
  // match text content
  if (a.text.trim() !== b.text.content.trim()) {
    return false;
  }

  if (isSharingDraftBendingTextConfiguration(a)) {
    // match bending angle
    if ((a.angle || 0) !== ((b as DraftBendingTextConfiguration).text.style.angle || 0)) {
      return false;
    }
  } else if (isSharingDraftSpecialTextConfiguration(a)) {
    const specialFontWidth =
      ((b as DraftSpecialTextConfiguration).task.size.width * b.scale * MM_PER_INCH) /
      b.printType.dpi;
    // the final special font image and the draft image can have different sizes
    //  when creating the product or draft we fix the scale when we know the final size
    // so when comparing a draft/product with the current state we need to give it some leeway
    if (Math.round(a.width) !== Math.round(specialFontWidth)) {
      return false;
    }
  } else if (isSharingDraftTextConfiguration(a) || isSharingDraftPlaceholderTextConfiguration(a)) {
    if (a.fontSize && a.fontSize !== (b as DraftTextConfiguration).text.style.fontSize) {
      return false;
    }
  }

  if (isSharingDraftSpecialTextConfiguration(a)) {
    if (
      // match special font family
      `specialfont_${a.fontId}` !== b.text.style.font.identifier ||
      // match text align (left | center | right | justify)
      a.textAlign !== b.text.style.textAlign
    ) {
      return false;
    }
  } else {
    if (
      // match sprd font family
      !isSameStoreFont(a, b.text.style.font) ||
      // match anchor (start | middle | end)
      (('textAnchor' in a && a.textAnchor) || 'start') !==
        textAlignToAnchor(b.text.style.textAlign as TextAlign) ||
      // match color
      hasDifferentColors(a, b) ||
      (a.fill && a.fill !== b.printColors.map((c) => c.fill).join(' '))
    ) {
      return false;
    }
  }

  return true;
}

function isSameConfiguration(a: SharingDraftConfiguration, b: DraftConfiguration) {
  if (!a || !b) {
    return false;
  }

  // compare primitive values
  if (
    a.type !== b.type ||
    a.printAreaId !== b.printArea.id ||
    a.printTypeId !== b.printType.id ||
    getRotationFromTransform(a.transform) !== b.rotation
  ) {
    return false;
  }

  if (!isEqualPoints(a.offset, b.offset)) {
    return false;
  }

  // compare the size, important for stickers
  const calcScaledSize = (dimension: number) => (dimension * b.scale).toFixed(2);
  if (
    a.width.toFixed(2) !== calcScaledSize(b.size.width) ||
    a.height.toFixed(2) !== calcScaledSize(b.size.height)
  ) {
    return false;
  }

  // compare design specific stuff
  if (
    isSharingDraftDesignConfiguration(a) &&
    !isSameDesignConfiguration(a, b as DraftDesignConfiguration)
  ) {
    return false;
  }

  // compare text stuff
  if (
    isSharingDraftConfigurationWithTextOrSpecialText(a) &&
    !isSameTextConfiguration(a, b as Exclude<DraftConfiguration, DraftDesignConfiguration>)
  ) {
    return false;
  }

  return true;
}

export const isSameDraft = (a: SharingDraft, b: Draft) => {
  if (!a || !b) {
    return false;
  }
  if (
    a.productTypeId !== b.productType.id ||
    a.appearanceId !== b.appearance.id ||
    a.viewId !== b.view.id
  ) {
    return false;
  }

  const configurationsA = a.configurations;
  const configurationCount = configurationsA.length;
  const configurationsB = getConfigurations(b);
  if (configurationCount !== configurationsB.length) {
    return false;
  }

  for (let configurationIndex = 0; configurationIndex < configurationCount; configurationIndex++) {
    if (
      !isSameConfiguration(configurationsA[configurationIndex], configurationsB[configurationIndex])
    ) {
      return false;
    }
  }

  return true;
};
